import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../environments/environment";

/**
 * Outcome of a call to the IPA Login UI API (BFF, repo ipa-login-ui-api), normalised for the
 * invite screens. Contract: docs/invite-flow.md.
 */
export type InvitationOutcome =
  | { kind: "valid" }
  | { kind: "activated"; redirect?: string }
  /** 401 INVALID_TOKEN: reset token expired or no longer valid. */
  | { kind: "expired" }
  /** 400 INVALID_INVITATION_LINK / BAD_REQUEST: the link cannot be decrypted or is incomplete. */
  | { kind: "invalidLink" }
  /** 400 PASSWORD_NOT_COMPLEX_ENOUGH: CDC rejected the password. */
  | { kind: "passwordRejected" }
  /** Not configured, network/CORS failure, APIM rejection, 5xx. */
  | { kind: "unavailable" };

/** The BFF answers with RFC 9457 problem details; the machine-readable code is in `title`. */
const PROBLEM_TITLES: Record<string, InvitationOutcome["kind"]> = {
  INVALID_TOKEN: "expired",
  INVALID_INVITATION_LINK: "invalidLink",
  BAD_REQUEST: "invalidLink",
  PASSWORD_NOT_COMPLEX_ENOUGH: "passwordRejected",
};

@Injectable({ providedIn: "root" })
export class InvitationService {
  constructor(private http: HttpClient) {}

  public get available(): boolean {
    return !!environment.invitationApiUrl;
  }

  public async validateInvite(token: string, origin: string): Promise<InvitationOutcome> {
    const res = await this.post("validateInvite", { token, origin });
    if (res.kind === "ok") {
      return res.body?.isValid === true ? { kind: "valid" } : { kind: "expired" };
    }
    return res.outcome;
  }

  public async completeInvitation(token: string, password: string, origin: string): Promise<InvitationOutcome> {
    const res = await this.post("completeInvitation", { token, password, origin });
    if (res.kind === "ok") {
      return { kind: "activated", redirect: typeof res.body?.redirect === "string" ? res.body.redirect : undefined };
    }
    return res.outcome;
  }

  private async post(
    action: "validateInvite" | "completeInvitation",
    body: object,
  ): Promise<{ kind: "ok"; body: any } | { kind: "error"; outcome: InvitationOutcome }> {
    if (!environment.invitationApiUrl) {
      console.warn(`InvitationService.${action} - no invitationApiUrl configured for this environment.`);
      return { kind: "error", outcome: { kind: "unavailable" } };
    }

    let headers = new HttpHeaders({ "Content-Type": "application/json" });
    if (environment.invitationApiSubscriptionKey) {
      headers = headers.set("Ocp-Apim-Subscription-Key", environment.invitationApiSubscriptionKey);
    }

    try {
      const res = await firstValueFrom(this.http.post<any>(`${environment.invitationApiUrl}/${action}`, body, { headers }));
      console.info(`InvitationService.${action} - success.`);
      return { kind: "ok", body: res };
    } catch (err) {
      const outcome = this.fromError(err);
      console.warn(`InvitationService.${action} - failed:`, outcome.kind, err instanceof HttpErrorResponse ? err.status : err);
      return { kind: "error", outcome };
    }
  }

  private fromError(err: unknown): InvitationOutcome {
    if (!(err instanceof HttpErrorResponse)) {
      return { kind: "unavailable" };
    }
    // Only trust a status that comes with the BFF's own problem title: APIM also answers 401
    // (missing/invalid subscription key), which must not be shown as "link expired".
    const title = typeof err.error?.title === "string" ? err.error.title : undefined;
    const kind = title ? PROBLEM_TITLES[title] : undefined;
    return kind ? ({ kind } as InvitationOutcome) : { kind: "unavailable" };
  }
}
